import { FC, useEffect, useState } from 'react';
import style from './EnquiryForm.module.scss';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { Body, Heading } from 'components/text';
import Container from 'components/container';
import Button from 'components/Button/Button';
import { ControlledTextField } from 'components/TextField/ControlledTextField';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  enquiryFormDefault,
  EnquiryFormSchema,
  enquiryFormSchema,
} from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import SearchableField from 'blocks/SearchableField';
import { getCityList } from 'api/misc';
import { CreateICLeadRequest } from 'types/types';
import { CloseIcon } from 'components/icons';
import {
  name,
  company_name,
  email,
  phone_number,
  enquiry_form_city_of_residennce,
  enquiry_form_travel_professional_question,
  how_did_you_hear_about_myler,
  enquire,
  partner_consortium_question,
  iata_number_question,
} from 'data/translationKeys';
import useLocalised from 'customHooks/useLocalised/useLocalised';
import { useMutation } from '@tanstack/react-query';
import { createMylerLead } from 'api/mylers';
import ControlledRadioButton from 'components/RadioButton/ControlledRadioButton';
import { IATAOptions } from '../contants';
import ToggleInput from '../components/toggle-input/ControlledToggleInput';
import { SelectOptionType } from 'components/Select/Select';
import ReferralChannelsSelect from '../components/referral-channel-select';

type EnquiryForm = {
  open: boolean;
  channels: SelectOptionType[];
  onSubmitSuccess: () => void;
  handleFormClose?: () => void;
};
const EnquiryForm: FC<EnquiryForm> = ({
  open,
  handleFormClose,
  onSubmitSuccess,
}) => {
  const nameText = useLocalised(name);
  const companyNameText = useLocalised(company_name);
  const phoneNumberText = useLocalised(phone_number);
  const emailText = useLocalised(email);
  const cityOfResidenceText = useLocalised(enquiry_form_city_of_residennce);
  const travelProfessionalText = useLocalised(
    enquiry_form_travel_professional_question
  );
  const channelText = useLocalised(how_did_you_hear_about_myler);
  const partnerConsortiumText = useLocalised(partner_consortium_question);
  const enquireText = useLocalised(enquire);
  const iataNumberQuestionText = useLocalised(iata_number_question);

  const methods = useForm<EnquiryFormSchema>({
    resolver: zodResolver(enquiryFormSchema),
    defaultValues: enquiryFormDefault,
    mode: 'onSubmit',
  });
  const {
    handleSubmit,
    control,
    setError,
    reset,
    clearErrors,
    trigger,
    formState: { errors },
    watch,
  } = methods;

  const [isLoading, setIsLoading] = useState(false);

  const handleDialogClose = () => {
    reset();
    handleFormClose && handleFormClose();
  };

  const createLeadMutation = useMutation(
    (data: CreateICLeadRequest) => createMylerLead(data),
    {
      onSuccess: () => {
        handleDialogClose();
        onSubmitSuccess();
      },
      onError: _ => {
        setError(
          'root.requestError',
          {
            message: '',
          },
          { shouldFocus: true }
        );
      },
    }
  );


  const onSubmit = async (data: EnquiryFormSchema) => {
    setIsLoading(true);
    const payload: CreateICLeadRequest = {
      city_id: data.city.id,
      name: data.name,
      is_professional: data.isProfessional,
      source: 'website',
      phone_number: data.phoneNumber,
      email: data.email,
      company_name: data.companyName,
      iata_type: data.iataType,
      iata_number: data.iataNumber,
      information_source: data.otherSource,
      additional_comments: data.notes,
      is_preferred_hotel_partner: !!data.partnerConsortiumName,
      preferred_hotel_partner_name: data.partnerConsortiumName,
      referral_channel_type_id: parseInt(data.channel?.split('-')[0]),
      referee: data.referralName,
    };

    createLeadMutation.mutate(payload, {
      onSuccess: () => {
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  const channelValue = watch('channel');
  const channelName = channelValue
    ? channelValue.split('-')[1].toLowerCase()
    : '';

  const isReferralSource = channelName === 'referral';
  const otherSource = channelName === 'other';

  return (
    <Dialog open={open} onClose={handleDialogClose} className={style.dialog}>
      <DialogContent className={style.container}>
        <Container className={style.closeBtnWrapper}>
          <IconButton
            aria-label="close"
            className={style.closeBtn}
            onClick={handleDialogClose}
          >
            <CloseIcon className={style.closeIcon} />
          </IconButton>
        </Container>

        <Container className={style.headerSection}>
          <Heading as="h4" align="center">
            Interested in becoming a MyLER?
          </Heading>
          <Body className={style.subheading} align="center">
            Complete a short form and a member of our team will be in touch with
            more information.
          </Body>
        </Container>

        <Container
          className={style.form}
          as="form"
          onChange={e => clearErrors('root.requestError')}
          onSubmit={e => handleSubmit(onSubmit)(e).catch(_ => {})}
        >
          <FormProvider {...methods}>
            <ControlledTextField label={nameText} type="text" name="name" />
            <ControlledTextField
              label={companyNameText + ' (optional)'}
              type="text"
              name="companyName"
            />
            <ControlledTextField label={emailText} type="email" name="email" />
            <ControlledTextField
              label={phoneNumberText + ' (optional)'}
              type="text"
              name="phoneNumber"
            />
            <Controller
              control={control}
              defaultValue={{ name: '', id: 0 }}
              name="city"
              render={({ field: { onChange, value, ref } }): any => {
                return (
                  <SearchableField
                    reference={ref}
                    name="city"
                    label={cityOfResidenceText}
                    queryFunction={getCityList}
                    onChange={onChange}
                    value={value}
                    errorMsg={
                      errors.city?.name?.message || errors.city?.id?.message
                    }
                  />
                );
              }}
            />
            <ToggleInput label={travelProfessionalText} name="isProfessional" />
            <ToggleInput label={iataNumberQuestionText} name="enterIataNumber">
              <ControlledRadioButton
                name="iataType"
                options={IATAOptions}
                defaultValue="iata"
                defaultChecked={true}
                className={style.iataType}
              />
              <ControlledTextField type="text" name="iataNumber" />
            </ToggleInput>
            <ToggleInput label={partnerConsortiumText} name="partnerConsortium">
              <ControlledTextField type="text" name="partnerConsortiumName" />
            </ToggleInput>

            <ReferralChannelsSelect label={channelText} name="channel" />

            {isReferralSource && (
              <ControlledTextField
                label="Referral name"
                type="text"
                name="referralName"
              />
            )}
            {otherSource && (
              <ControlledTextField type="text" name="otherSource" />
            )}

            <ControlledTextField
              label="Anything else to share with us..."
              type="text"
              name="notes"
              multiline
              minRows={3}
            />

            <Container className={style.enquireBtnWrapper}>
              <Button
                htmlAttributes={{
                  disabled: isLoading,
                }}
                title={enquireText}
                onClick={() => {}}
                isLoading={isLoading}
                className={style.enquireBtn}
              />
            </Container>
            {errors.root?.requestError && (
              <Body color={'forms-red'} mt={10}>
                There has been an issue with submitting your enquiry form,
                please try again or alternatively contact us for assistance{' '}
                <a
                  href="mailto:myler@littleemperors.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  myler@littleemperors.com
                </a>
              </Body>
            )}
          </FormProvider>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryForm;
